import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { getApiErrorMessage, MUTATION_OFFLINE_MESSAGE, isNetworkFailure } from '@/lib/network/apiErrors';
import { getNetworkState } from '@/lib/network/connectivity';
import { useNetwork } from '@/lib/network/NetworkProvider';
import MobileShell from '@/components/MobileShell';
import ResqlyCard from '@/components/resqly/ResqlyCard';
import ResqlyStatus from '@/components/resqly/ResqlyStatus';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatIncomingOffer } from '@/lib/providerDispatch';
import { formatExpiresSeconds } from '@/lib/providerPush';
import {
  setActiveDispatchOffer,
  stopDispatchRingtone,
} from '@/lib/dispatchRingtone';

const OFFER_REVALIDATE_MS = 4000;

function isActionable(fresh) {
  if (!fresh) return false;
  if (fresh.status !== 'pending' && fresh.status !== 'viewed') return false;
  const expiresAt = fresh.offer_payload?.expires_at;
  if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) return false;
  return true;
}

export default function ProviderOfferDetail() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [expiresSec, setExpiresSec] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/providers/me/offers/${offerId}`);
        setOffer(data.offer);
      } catch {
        toast.error('Offer not found');
        navigate('/provider/offers');
      } finally {
        setLoading(false);
      }
    })();
  }, [offerId, navigate]);

  // Revalidate the offer every few seconds: the detail view must disappear
  // immediately when the offer is accepted elsewhere, declined, cancelled
  // (superseded), expired, or past its expires_at — without waiting for the
  // local countdown to reach zero.
  useEffect(() => {
    if (!offerId) return undefined;
    let cancelled = false;
    const revalidate = async () => {
      try {
        const { data } = await api.get(`/providers/me/offers/${offerId}`);
        if (cancelled) return;
        const fresh = data.offer;
        if (!isActionable(fresh)) {
          stopDispatchRingtone(`offer-invalid:${fresh?.status || 'missing'}`);
          navigate('/provider/offers', { replace: true });
          return;
        }
        setActiveDispatchOffer(offerId);
        setOffer((prev) => (
          prev && prev.status === fresh.status && prev.updated_at === fresh.updated_at
            ? prev
            : fresh
        ));
      } catch {
        // Transient failures must not eject the provider mid-decision.
      }
    };
    const timer = setInterval(revalidate, OFFER_REVALIDATE_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [offerId, navigate]);

  useEffect(() => {
    const view = offer ? formatIncomingOffer(offer) : null;
    if (!view?.expiresAt) return undefined;
    const tick = () => setExpiresSec(formatExpiresSeconds(view.expiresAt));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [offer]);

  const { isOnline } = useNetwork();

  const refreshOffer = async () => {
    try {
      const { data } = await api.get(`/providers/me/offers/${offerId}`);
      setOffer(data.offer);
      return data.offer;
    } catch {
      return null;
    }
  };

  const accept = async () => {
    if (!getNetworkState().isOnline) {
      toast.error(MUTATION_OFFLINE_MESSAGE);
      return;
    }
    setActing(true);
    try {
      // The accept response includes the created booking (backend:
      // routes_provider._accept returns public_booking_document). Navigate
      // straight to the booking detail so START TRIP is reachable immediately.
      const { data } = await api.post(`/providers/me/offers/${offerId}/accept`);
      stopDispatchRingtone('accepted');
      toast.success('Offer accepted');
      const bookingId = data?.booking?.id;
      navigate(bookingId ? `/provider/bookings/${bookingId}` : '/provider/bookings', {
        replace: true,
      });
    } catch (e) {
      if (isNetworkFailure(e)) {
        toast.error(MUTATION_OFFLINE_MESSAGE);
        await refreshOffer();
      } else {
        toast.error(getApiErrorMessage(e, 'Could not accept'));
        await refreshOffer();
      }
    } finally {
      setActing(false);
    }
  };

  const decline = async () => {
    if (!getNetworkState().isOnline) {
      toast.error(MUTATION_OFFLINE_MESSAGE);
      return;
    }
    setActing(true);
    try {
      await api.post(`/providers/me/offers/${offerId}/decline`);
      stopDispatchRingtone('declined');
      toast.success('Offer declined');
      navigate('/provider/offers');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not decline'));
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <MobileShell title="Offer">
        <div className="p-5 text-slate-500 text-sm">Loading…</div>
      </MobileShell>
    );
  }

  if (!offer) return null;

  const canRespond = offer.status === 'pending' || offer.status === 'viewed';
  const view = formatIncomingOffer(offer);

  return (
    <MobileShell title="Incoming request" hideBack={false}>
      <div className="px-5 py-5 space-y-4 min-h-[70vh]" data-testid="provider-offer-detail">
        {canRespond && expiresSec != null && (
          <div className="rounded-2xl overflow-hidden border border-amber-200 bg-amber-50">
            <div
              className="h-1 bg-amber-400 transition-all duration-1000 ease-linear"
              style={{ width: `${Math.max(0, Math.min(100, (expiresSec / 60) * 100))}%` }}
            />
            <div className="px-4 py-3 flex items-center justify-between">
              <span className="text-sm font-medium text-amber-900">Respond soon</span>
              <span className="text-lg font-bold tabular-nums text-amber-800" data-testid="incoming-expires">
                {expiresSec}s
              </span>
            </div>
          </div>
        )}

        <ResqlyCard className="p-5 space-y-4 shadow-[var(--resqly-shadow-md)]" elevated>
          <ResqlyStatus tone="primary">Incoming request</ResqlyStatus>
          <div>
            <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">Service</div>
            <div className="font-bold text-2xl text-slate-900 mt-1 tracking-tight" data-testid="incoming-service-name">
              {view.serviceName}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Metric label="Distance" value={view.distanceLabel} testid="incoming-distance" />
            {view.durationDisplay && (
              <Metric label="Duration" value={view.durationDisplay} testid="incoming-duration" />
            )}
            {view.caseTypeDisplay && (
              <Metric label="Case type" value={view.caseTypeDisplay} testid="incoming-case-type" className="col-span-2" />
            )}
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-2">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Your earnings</div>
            {view.baseServiceAmount != null && (
              <div className="flex justify-between text-sm mt-2" data-testid="incoming-base-service">
                <span className="text-slate-600">Base service</span>
                <span className="font-semibold text-slate-900">₹{view.baseServiceAmount}</span>
              </div>
            )}
            {view.travelAmount != null && (
              <div className="flex justify-between text-sm mt-1" data-testid="incoming-travel">
                <span className="text-slate-600">Travel</span>
                <span className="font-semibold text-slate-900">₹{view.travelAmount}</span>
              </div>
            )}
          </div>

          {offer.offer_payload?.preferences_summary && (
            <div className="text-sm text-slate-600 border-t border-slate-100 pt-3">
              <span className="font-medium text-slate-800">Notes: </span>
              {offer.offer_payload.preferences_summary}
            </div>
          )}
          <p className="text-xs text-slate-400">
            Customer details are shared after you accept.
          </p>
        </ResqlyCard>

        {canRespond && (
          <div className="flex flex-col gap-3 pt-2">
            <Button
              data-testid="provider-accept-offer"
              onClick={accept}
              disabled={acting || !isOnline}
              className="w-full h-12 rounded-xl bg-blue-700 hover:bg-blue-800 text-base font-semibold shadow-lg shadow-blue-700/25"
            >
              Accept request
            </Button>
            <Button
              data-testid="provider-decline-offer"
              variant="outline"
              onClick={decline}
              disabled={acting}
              className="w-full h-11 rounded-xl border-slate-200 text-slate-600"
            >
              Decline
            </Button>
          </div>
        )}
      </div>
    </MobileShell>
  );
}

function Metric({ label, value, testid, className = '' }) {
  return (
    <div className={`rounded-xl bg-white border border-slate-100 p-3 ${className}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
